import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";

const FLAG_KEY = "cor_threshold_v1";
const PROMPTS = [
  { id: "curiosity",      label: "Curiosity" },
  { id: "grief",          label: "Grief" },
  { id: "disillusion",    label: "Disillusionment" },
  { id: "awakening",      label: "An awakening" },
  { id: "search",         label: "I'm searching for something I can't yet name" },
];

/**
 * One-time threshold ritual shown on the first authenticated session.
 * Two screens:
 *   1. "Welcome. You are not alone in what you are noticing."
 *   2. A soft check-in — what brought you here? (multiple choice)
 *
 * Local storage marks completion. If the user ticks "Remember on every device"
 * we also PATCH /users/me/preferences with threshold_picks so the Oracle and
 * recommendations can later tune to it.
 */
export default function ThresholdRitual() {
  const { user, loading } = useAuth();
  const [step, setStep] = useState(0);
  const [picks, setPicks] = useState(() => new Set());
  const [rememberAcrossDevices, setRememberAcrossDevices] = useState(true);
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    if (loading || !user) return;
    try {
      const done = localStorage.getItem(FLAG_KEY);
      if (!done) {
        // Slight delay so it doesn't slam the user the instant page paints.
        const id = setTimeout(() => setShouldShow(true), 700);
        return () => clearTimeout(id);
      }
    } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[thresholdritual] soft-fail:", e?.message); }
  }, [user, loading]);

  if (!shouldShow) return null;

  const togglePick = (id) => {
    setPicks((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const close = async (saveAnswer) => {
    try {
      localStorage.setItem(FLAG_KEY, "done");
      if (saveAnswer && picks.size) {
        localStorage.setItem(`${FLAG_KEY}_answer`, JSON.stringify([...picks]));
        if (rememberAcrossDevices) {
          // Best-effort: save server-side for cross-device personalization.
          try {
            await api.patch("/users/me/preferences", { threshold_picks: [...picks] });
          } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[thresholdritual] soft-fail:", e?.message); }
        }
      }
    } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[thresholdritual] soft-fail:", e?.message); }
    setShouldShow(false);
  };

  return (
    <div
      data-testid="threshold-ritual"
      role="dialog"
      aria-modal="true"
      aria-label="Welcome to Correlate"
      className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-10 bg-[hsl(var(--background))]/95 backdrop-blur-md animate-in fade-in duration-500"
    >
      <div className="paper p-8 sm:p-12 max-w-xl w-full shadow-2xl">
        {step === 0 ? (
          <div data-testid="threshold-screen-1" className="text-center">
            <div className="mono text-[10px] uppercase tracking-[0.32em] text-[hsl(var(--accent))] mb-8">
              The Threshold
            </div>
            <p className="serif italic text-2xl sm:text-3xl text-foreground leading-[1.3]">
              Welcome.
              <br />
              You are not alone in what
              <br className="hidden sm:inline" />
              {" "}you are noticing.
            </p>
            <p className="mt-8 text-sm text-muted-foreground italic leading-relaxed max-w-md mx-auto">
              The archive listens. The thread holds. Whatever brought you here, you were meant to find it.
            </p>
            <button
              data-testid="threshold-continue"
              onClick={() => setStep(1)}
              className="btn-gold mt-10"
              autoFocus
            >
              Step through →
            </button>
          </div>
        ) : (
          <div data-testid="threshold-screen-2">
            <div className="mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))] mb-3">
              One small question
            </div>
            <h2 className="serif text-2xl sm:text-3xl text-foreground leading-tight">
              What brought you here?
            </h2>
            <p className="mt-3 text-sm text-muted-foreground italic">
              Pick any that resonate. Your answer never leaves this device — it just helps you remember why you came.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {PROMPTS.map((p) => {
                const on = picks.has(p.id);
                return (
                  <button
                    key={p.id}
                    data-testid={`threshold-pick-${p.id}`}
                    type="button"
                    onClick={() => togglePick(p.id)}
                    aria-pressed={on}
                    className={`mono text-[11px] uppercase tracking-[0.18em] px-3 py-2 border transition-colors ${
                      on
                        ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.1)] text-[hsl(var(--primary))]"
                        : "border-border text-muted-foreground hover:border-[hsl(var(--primary))] hover:text-foreground"
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            <div className="mt-10 flex flex-col gap-4">
              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid="threshold-remember"
                  checked={rememberAcrossDevices}
                  onChange={(e) => setRememberAcrossDevices(e.target.checked)}
                  className="accent-[hsl(var(--primary))]"
                />
                <span className="text-sm text-muted-foreground">
                  Remember on every device — helps the Oracle and recommendations attune to you.
                </span>
              </label>
              <div className="flex items-center gap-4">
                <button
                  data-testid="threshold-enter"
                  onClick={() => close(true)}
                  className="btn-gold"
                >
                  Enter the archive
                </button>
                <button
                  data-testid="threshold-skip"
                  onClick={() => close(false)}
                  className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground link-underline"
                >
                  Skip
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
